import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { AuthStatusResponse } from '@shared/api';
import { authLogin, authLogout, authSelectOrganization, authStatus } from '../api/client';

export function userFacingAuthError(cause: unknown): string {
  const message = cause instanceof Error ? cause.message : String(cause);
  const normalized = message.toLowerCase();

  if (normalized.includes('access_denied') || normalized.includes('does not have access')) {
    return 'This account does not have access to Proposals. Ask an administrator to add you.';
  }
  if (normalized.includes('invalid_credentials') || normalized.includes('login failed')) {
    return 'That email or password is not correct.';
  }
  if (normalized.includes('new_password_required')) {
    return 'Your account needs a new password. Complete the reset in the VisitIQ app, then try again.';
  }
  if (normalized.includes('not confirmed') || normalized.includes('unconfirmed')) {
    return 'Confirm your email address before signing in.';
  }
  if (
    normalized.includes('too many') ||
    normalized.includes('throttl') ||
    normalized.includes('limit exceeded')
  ) {
    return 'Too many sign-in attempts. Wait a moment, then try again.';
  }
  if (
    normalized.includes('could not reach') ||
    normalized.includes('network') ||
    normalized.includes('failed to fetch')
  ) {
    return 'We could not reach the sign-in service. Check your connection and try again.';
  }
  if (normalized.includes('refresh_failed') || normalized.includes('session')) {
    return 'Your session has expired. Sign in again to continue.';
  }

  return 'We could not sign you in. Try again or contact your administrator.';
}

export interface AuthStore {
  status: AuthStatusResponse | null;
  error: string | null;
  busy: boolean;
  /** The password is handed straight to the API call and never held here. */
  signIn: (username: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  selectOrganization: (organizationId: string | null, cobrand?: string | null) => Promise<void>;
  clearError: () => void;
}

/**
 * One sign-in status for the whole app. Every `useAuth()` caller (AuthGate,
 * the sidebar, views) reads the same value, and `/api/auth/status` is asked
 * once per page load — not once per mount, which blanked the screen on every
 * in-app navigation while a fresh copy resolved.
 */
let sharedStatus: AuthStatusResponse | null = null;
let statusRequested = false;
const statusListeners = new Set<() => void>();

function setSharedStatus(next: AuthStatusResponse | null): void {
  sharedStatus = next;
  for (const listener of statusListeners) listener();
}

function subscribeStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
}

const readStatus = () => sharedStatus;

export function useAuth(): AuthStore {
  const status = useSyncExternalStore(subscribeStatus, readStatus);
  const setStatus = setSharedStatus;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (statusRequested) return;
    statusRequested = true;
    void authStatus()
      .then(setSharedStatus)
      .catch(() => {
        // A missing auth endpoint must not break the workspace; the chip just
        // stays quiet. Allow a later mount to ask again.
        statusRequested = false;
      });
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await authLogin(username, password));
      return true;
    } catch (cause) {
      setError(userFacingAuthError(cause));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setBusy(true);
    try {
      setStatus(await authLogout());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }, []);

  const selectOrganization = useCallback(async (
    organizationId: string | null,
    cobrand: string | null = null,
  ) => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await authSelectOrganization(organizationId, cobrand));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    status,
    error,
    busy,
    signIn,
    signOut,
    selectOrganization,
    clearError: () => setError(null),
  };
}
